import { z } from 'zod';
import { Validation } from '@mews/framework';
import { LearningPath, UserStatus } from '@mews-commander/data-access';
import { EnterpriseProfilePrivilegesEnumNames } from '@mews-commander/legacy-data-structures';
import { isPrivilegeValue } from '@mews-commander/legacy-entities/src/utils';
import { MULTIPLE_VALUES, RolesAndPermissionsOption } from '../../types';
import { FieldNames } from '../FieldNames';

export const createUserEditSchema = ({
    requireName,
    requireLearningPath = true,
}: {
    requireName: boolean;
    requireLearningPath?: boolean;
}) => {
    const baseSchema = z.object({
        [FieldNames.FirstName]: requireName ? Validation.String.Required : z.string().optional(),
        [FieldNames.LastName]: requireName ? Validation.String.Required : z.string().optional(),
        // "Locked" Learning path needs to be undefined, BE doesn't know MULTIPLE_VALUES. See LearningPathLockableField
        [FieldNames.AssignedLearningPath]: requireLearningPath
            ? z.nativeEnum(LearningPath).or(z.literal(MULTIPLE_VALUES))
            : z.nativeEnum(LearningPath).or(z.literal(MULTIPLE_VALUES)).optional(),
        [FieldNames.SuperiorProfileId]: z.string().optional(),
        [FieldNames.DepartmentId]: z.string().optional(),
        [FieldNames.Cashiers]: z.array(z.string()).optional(),
        [FieldNames.Status]: z.nativeEnum(UserStatus).optional(),
        [FieldNames.ReceiveCustomerMessages]: z.boolean().optional(),
        [FieldNames.Reset2FA]: z.boolean().optional(),
        [FieldNames.RoleIds]: z.array(z.string()).optional(),
        [FieldNames.Privileges]: z.array(z.custom<EnterpriseProfilePrivilegesEnumNames>(isPrivilegeValue)).optional(),
        [FieldNames.EmployeeIncentiveCode]: z.string().optional(),
    });

    return z.discriminatedUnion(FieldNames.RolesAndPermissions, [
        baseSchema.extend({
            [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.Roles),
            [FieldNames.RoleIds]: z.array(z.string()).min(1),
        }),
        baseSchema.extend({
            [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.CustomizedPermissions),
            [FieldNames.Privileges]: z.array(z.custom<EnterpriseProfilePrivilegesEnumNames>(isPrivilegeValue)).min(1),
        }),
        baseSchema.extend({
            [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.Admin),
        }),
        baseSchema.extend({
            [FieldNames.RolesAndPermissions]: z.literal(RolesAndPermissionsOption.NoPermissions),
        }),
        // Special case when user is in multiple properties and has different permissions in each
        baseSchema.extend({
            [FieldNames.RolesAndPermissions]: z.literal(undefined),
        }),
    ]);
};

export type EditUserFormValues = z.infer<ReturnType<typeof createUserEditSchema>>;
